import ReactMarkdown, { type Components } from "react-markdown";
import { cn } from "@/lib/utils";

/**
 * Compact markdown renderer for assistant / AI-generated text.
 *
 * Why this exists: Solomon (and other AI surfaces) return markdown
 * (`**bold**`, `- bullets`, headings, links). Rendered as a raw string
 * the syntax leaks through visually. This turns it into real elements.
 *
 * Styling is applied per-element rather than via the `prose` typography
 * plugin so the spacing stays tight inside chat bubbles (prose is tuned
 * for long-form articles and adds large vertical rhythm) and so the same
 * component can be mirrored into the Relay app, which doesn't ship the
 * typography plugin.
 *
 * Scope: CommonMark only (no remark-gfm) — bold, italic, headings, lists,
 * links, inline/block code, blockquote, hr. GFM tables/strikethrough are
 * not parsed; add remark-gfm here if the models start emitting them.
 */
const markdownComponents: Components = {
  p: ({ children }) => <p className="mb-2 leading-relaxed last:mb-0">{children}</p>,
  ul: ({ children }) => (
    <ul className="mb-2 ml-4 list-disc space-y-1 last:mb-0">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="mb-2 ml-4 list-decimal space-y-1 last:mb-0">{children}</ol>
  ),
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  h1: ({ children }) => (
    <h1 className="mb-2 mt-3 text-base font-semibold first:mt-0">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="mb-2 mt-3 text-sm font-semibold first:mt-0">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="mb-1 mt-2 text-sm font-semibold first:mt-0">{children}</h3>
  ),
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium underline underline-offset-2 hover:opacity-80"
    >
      {children}
    </a>
  ),
  // Never render images from AI-generated markdown: an injected prompt could
  // make the browser fetch an attacker URL and leak request metadata.
  img: () => null,
  code: ({ className, children }) => {
    // react-markdown v9 drops the `inline` prop; inline code has no language
    // className while fenced blocks do. Matches the project convention in
    // CommentThread.tsx.
    const isInline = !className;
    if (isInline) {
      return (
        <code className="rounded bg-black/10 px-1 py-0.5 text-[0.85em] dark:bg-white/10">
          {children}
        </code>
      );
    }
    return <code className={className}>{children}</code>;
  },
  pre: ({ children }) => (
    <pre className="mb-2 overflow-x-auto rounded-md bg-black/10 p-2.5 text-xs last:mb-0 dark:bg-white/10">
      {children}
    </pre>
  ),
  blockquote: ({ children }) => (
    <blockquote className="mb-2 border-l-2 border-current/30 pl-3 italic opacity-90 last:mb-0">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-3 border-border" />,
};

interface MarkdownProps {
  children: string;
  className?: string;
}

export function Markdown({ children, className }: MarkdownProps) {
  return (
    <div className={cn("text-sm", className)}>
      <ReactMarkdown components={markdownComponents}>{children}</ReactMarkdown>
    </div>
  );
}
